import React, { useState } from 'react';
import { LanguageToggle } from '../components/LanguageToggle';
import { WhatsAppButton, StickyWhatsAppButton } from '../components/WhatsAppButton';
import { TikTokCarousel } from '../components/TikTokCarousel';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { content, tiktokVideos, whatsappNumbers } from '../mockData';
import { 
  CheckCircle, 
  Shield, 
  Users, 
  Award, 
  MapPin, 
  GraduationCap,
  Heart,
  Phone,
  Facebook
} from 'lucide-react';

export default function LandingPage() {
  const [lang, setLang] = useState('en');
  const t = content[lang];
  const isRTL = lang === 'ar';

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className={`min-h-screen bg-[#FFF8F1] ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header/Navigation */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#A0856E]/20">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img
                src="https://customer-assets.emergentagent.com/job_admission-guided/artifacts/5s6lqwa7_Original%20on%20Transparent.png"
                alt="Marhaba Poland"
                className="h-12 md:h-16 w-auto"
              />
            </div>
            
            <div className="flex items-center gap-4">
              <button
                onClick={() => scrollToSection('process')}
                className="hidden md:inline-block text-[#6B5949] hover:text-[#AA6A03] transition-colors font-medium"
              >
                {t.nav.learnMore}
              </button>
              <WhatsAppButton
                phoneNumber={whatsappNumbers[lang === 'ar' ? 'arabic' : 'english']}
                label={t.nav.contactWhatsApp}
                primary
              />
              <LanguageToggle currentLang={lang} onToggle={() => setLang(lang === 'en' ? 'ar' : 'en')} />
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_admission-guided/artifacts/3x7k4yf0_DSC06254-2.jpg"
            alt="Marhaba Poland Conference"
            className="w-full h-full object-cover opacity-15"
          />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-[#E5C09D] text-[#2D1E13] hover:bg-[#E5C09D]/90 px-4 py-2 text-sm font-semibold">
              {t.about.subheadline}
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold text-[#2D1E13] mb-6 leading-tight">
              {t.hero.headline}
            </h1>
            
            <p className="text-xl md:text-2xl text-[#6B5949] mb-10 max-w-3xl mx-auto leading-relaxed">
              {t.hero.subheadline}
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <WhatsAppButton
                phoneNumber={whatsappNumbers[lang === 'ar' ? 'arabic' : 'english']}
                label={t.hero.ctaPrimary}
                primary
              />
              <button
                onClick={() => scrollToSection('process')}
                className="px-6 py-3 bg-white text-[#AA6A03] border-2 border-[#AA6A03] rounded-lg font-semibold
                           hover:bg-[#FFF8F1] transition-all duration-300"
              >
                {t.hero.ctaSecondary}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust/Problem Section */}
      <section className="py-20 bg-[#FFFFFF]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold text-[#2D1E13] text-center mb-12">
              {t.trust.headline}
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {t.trust.problems.map((problem, index) => (
                <Card key={index} className="border-[#A0856E]/20 hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-[#D24F08] text-xl">{problem.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[#6B5949]">{problem.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <div className="bg-gradient-to-r from-[#FFF8F1] to-[#E5C09D]/30 p-8 rounded-xl text-center border-2 border-[#AA6A03]">
              <p className="text-lg md:text-xl text-[#2D1E13] font-semibold">
                {t.trust.solution}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-[#FFF8F1]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-[#2D1E13] mb-4">
              {t.about.headline}
            </h2>
            <p className="text-xl text-[#AA6A03] font-semibold">
              {t.about.subheadline}
            </p>
          </div>

          {/* Team Photos Showcase */}
          <div className="grid md:grid-cols-3 gap-8 mb-16 max-w-6xl mx-auto">
            <div className="relative rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_admission-guided/artifacts/i5jbrw2u_DSC06118.jpg"
                alt="Marhaba Poland Team"
                className="w-full h-80 object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="text-white font-bold text-lg">
                  {lang === 'en' ? 'Our Expert Team' : 'فريقنا الخبير'}
                </p>
                <p className="text-white/90 text-sm">
                  {lang === 'en' ? 'Dedicated to your success' : 'ملتزمون بنجاحك'}
                </p>
              </div>
            </div>
            
            <div className="relative rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_admission-guided/artifacts/j6l3isxn_DSC05755-2.jpg"
                alt="Conference Participation"
                className="w-full h-80 object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="text-white font-bold text-lg">
                  {lang === 'en' ? 'University Partnerships' : 'شراكات جامعية'}
                </p>
                <p className="text-white/90 text-sm">
                  {lang === 'en' ? 'Trusted by top institutions' : 'موثوقون من أفضل المؤسسات'}
                </p>
              </div>
            </div>
            
            <div className="relative rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_admission-guided/artifacts/hifhkohj_DSC05235.jpg"
                alt="Personal Consultation"
                className="w-full h-80 object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="text-white font-bold text-lg">
                  {lang === 'en' ? 'Personal Guidance' : 'إرشاد شخصي'}
                </p>
                <p className="text-white/90 text-sm">
                  {lang === 'en' ? 'One-on-one support for families' : 'دعم فردي للعائلات'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {t.about.features.map((feature, index) => {
              const icons = [Award, Users, MapPin, GraduationCap, Shield, CheckCircle];
              const Icon = icons[index] || Award;
              
              return (
                <Card key={index} className="border-[#A0856E]/20 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <CardHeader>
                    <Icon className="h-12 w-12 text-[#AA6A03] mb-4" />
                    <CardTitle className="text-[#2D1E13]">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[#6B5949]">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-20 bg-[#FFFFFF]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-[#AA6A03] text-white px-4 py-2 text-sm font-bold">
              {t.process.successBadge}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold text-[#2D1E13]">
              {t.process.headline}
            </h2>
          </div>
          
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {t.process.steps.map((step, index) => (
                <div 
                  key={index} 
                  className="relative p-8 bg-gradient-to-br from-[#FFF8F1] to-white rounded-xl border-2 border-[#A0856E]/20
                             hover:border-[#AA6A03] hover:shadow-xl transition-all duration-300"
                >
                  <div className="text-6xl font-bold text-[#E5C09D] mb-4">{step.number}</div>
                  <h3 className="text-2xl font-bold text-[#2D1E13] mb-3">{step.title}</h3>
                  <p className="text-[#6B5949]">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-20 bg-[#FFF8F1]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-[#2D1E13] mb-4">
              {t.packages.headline}
            </h2>
            <p className="text-lg text-[#6B5949]">{t.packages.subheadline}</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-8">
            {t.packages.packages.map((pkg, index) => (
              <Card 
                key={index} 
                className={`relative overflow-hidden hover:shadow-2xl transition-all duration-300 ${
                  pkg.popular 
                    ? 'border-4 border-[#AA6A03] scale-105' 
                    : 'border-[#A0856E]/20'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute top-0 right-0 bg-[#AA6A03] text-white px-4 py-2 text-sm font-bold
                                  transform translate-x-8 translate-y-4 rotate-45">
                    {lang === 'en' ? 'POPULAR' : 'الأكثر شعبية'}
                  </div>
                )}
                
                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl text-[#2D1E13]">{pkg.name}</CardTitle>
                  <CardDescription className="text-[#6B5949]">{pkg.description}</CardDescription>
                  <p className="text-lg font-semibold text-[#AA6A03] mt-2">{pkg.price}</p>
                </CardHeader>
                
                <CardContent>
                  <ul className="space-y-3">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-[#AA6A03] flex-shrink-0 mt-0.5" />
                        <span className="text-[#6B5949]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <p className="text-center text-[#6B5949] text-lg font-semibold">
            {t.packages.note}
          </p>
        </div>
      </section>

      {/* Safety Section */}
      <section className="py-20 bg-[#FFFFFF] relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-5">
          <img
            src="https://customer-assets.emergentagent.com/job_admission-guided/artifacts/hzrd2f30_DSC06083-2.jpg"
            alt="Support Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Heart className="h-16 w-16 text-[#D24F08] mx-auto mb-6" />
              <h2 className="text-3xl md:text-5xl font-bold text-[#2D1E13] mb-6">
                {t.safety.headline}
              </h2>
              <p className="text-xl text-[#6B5949] leading-relaxed">
                {t.safety.description}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {t.safety.supports.map((support, index) => (
                <div key={index} className="p-6 bg-[#FFF8F1] rounded-xl border border-[#A0856E]/20">
                  <h3 className="text-xl font-bold text-[#AA6A03] mb-2">{support.title}</h3>
                  <p className="text-[#6B5949]">{support.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TikTok Carousel */}
      <TikTokCarousel videos={tiktokVideos} lang={lang} />

      {/* FAQ Section */}
      <section className="py-20 bg-[#FFF8F1]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold text-[#2D1E13] text-center mb-12">
              {t.faq.headline}
            </h2>
            
            <Accordion type="single" collapsible className="space-y-4">
              {t.faq.questions.map((item, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white border-2 border-[#A0856E]/20 rounded-lg px-6 hover:border-[#AA6A03] transition-colors"
                >
                  <AccordionTrigger className="text-left text-[#2D1E13] font-semibold hover:text-[#AA6A03] hover:no-underline">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#6B5949] leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 bg-[#FFFFFF]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-[#2D1E13] text-center mb-10">
            {t.socialProof.headline}
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {t.socialProof.testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="p-6 bg-gradient-to-br from-[#FFF8F1] to-[#E5C09D]/20 rounded-lg text-center
                           border border-[#A0856E]/20 hover:shadow-lg transition-shadow"
              >
                <p className="text-[#2D1E13] font-semibold">{testimonial}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#AA6A03] to-[#D24F08] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              {t.cta.headline}
            </h2>
            <p className="text-xl mb-10 opacity-95">
              {t.cta.subheadline}
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <button
                onClick={() => {
                  const num = whatsappNumbers[lang === 'ar' ? 'arabic' : 'english'].replace(/\s+/g, '');
                  window.open(`https://wa.me/${num}`, '_blank');
                }}
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#AA6A03] rounded-lg font-bold
                           text-lg hover:bg-[#FFF8F1] transition-all duration-300 transform hover:scale-105 shadow-xl"
              >
                <Phone className="h-6 w-6" />
                {t.cta.ctaWhatsApp}
              </button>
              
              <button
                onClick={() => window.open('https://www.facebook.com', '_blank')}
                className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-sm text-white 
                           border-2 border-white rounded-lg font-bold text-lg hover:bg-white hover:text-[#AA6A03]
                           transition-all duration-300 transform hover:scale-105"
              >
                <Facebook className="h-6 w-6" />
                {t.cta.ctaMessenger}
              </button>
            </div>
            
            <p className="text-sm opacity-90">{t.cta.note}</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2D1E13] text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_admission-guided/artifacts/groklvtz_White%20on%20Transparent.png"
                alt="Marhaba Poland"
                className="h-16 md:h-20 w-auto mb-4"
              />
              <p className="text-white/80">{t.footer.tagline}</p>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">{t.footer.contact}</h4>
              <div className="space-y-2 text-white/80">
                <p>{t.footer.whatsappArabic}: {whatsappNumbers.arabic}</p>
                <p>{t.footer.whatsappEnglish}: {whatsappNumbers.english}</p>
              </div>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">{t.footer.social}</h4>
              <div className="flex gap-4">
                <a 
                  href="https://www.tiktok.com/@seham_szymanska" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white/80 hover:text-[#AA6A03] transition-colors"
                >
                  TikTok
                </a>
                <a 
                  href="https://www.facebook.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white/80 hover:text-[#AA6A03] transition-colors"
                >
                  Facebook
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-white/20 pt-6 text-center text-white/60">
            <p>{t.footer.rights}</p>
          </div>
        </div>
      </footer>

      {/* Sticky WhatsApp Button */}
      <StickyWhatsAppButton phoneNumber={whatsappNumbers[lang === 'ar' ? 'arabic' : 'english']} />
    </div>
  );
}
